import { Text, useAlert } from '@ebh/capsule';
import { useScreen } from '@ebh/capsule/hooks';
import { ArrowChevronDown, MenuIcon } from '@ebh/capsule/icons';
import { PersonOutline } from '@vhc-portal/common/components';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useGetCmsData from '../../api/hooks/cms/use-get-cms-data';
import { queryClient } from '../../api/query-client';
import cookiesManager from '../../utils/cookies';
import { AUTH_KEYS } from '../../utils/cookies/keys';
import { ProfileSelection } from '../profile-selection';
import Sheet from '../sheet';
import { CreateNewPassword } from '../change-password';
import { usePermission } from '../../permissions/use-permission';
import { PERMISSIONS_ID_MAP } from '../../utils/permissions';
import { downloadFileFromCms } from '../../utils/download-file';
import styles from './styles';
import { STATIC_IMAGES } from '../../utils/images-mapping';


interface NavLinks {
  Title: string;
  MetaData: {
    url: string;
    icon: string;
    identifier?: string;
    showInProfile?: boolean;
  };
  UniqueComponentId: string;
}

const Navbar = () => {
  const params = {
    pageType: 'HR_Landing Page',
    pageName: 'navbar',
    collectionType: 'vidal-product-page-layout',
  };
  const navigate = useNavigate();
  const cachedData = queryClient.getQueryData(['cmsData', params]);
  const isLogin = cookiesManager.get(AUTH_KEYS.ACCESS_TOKEN);
  const { hasPermission } = usePermission();

  const { cmsData, isCmsDataLoading } = useGetCmsData({
    params,
    enabled: !!isLogin && !cachedData,
  });

  const navLinks = useMemo(() => {
    const filteredLinks = cmsData?.data?.[0]?.PageLayout?.[0]?.Bullets?.filter(
      (item: NavLinks | null | undefined) =>
        item &&
        Object.keys(item).length > 0 &&
        !item.MetaData?.showInProfile &&
        item.Title?.trim() &&
        item.MetaData?.url?.trim(),
    ) as NavLinks[] | undefined;

    return filteredLinks?.filter((item) => {
      const requiredPermission = PERMISSIONS_ID_MAP[item.UniqueComponentId];
      if (!requiredPermission) return true;
      return hasPermission(requiredPermission);
    });
  }, [cmsData, hasPermission]);

  const isMobile = !useScreen('md');

  const [isProfileOpen, setProfileOpen] = useState(false);
  const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const profileRef = useRef<HTMLDivElement>(null);
  const successAlert = useAlert({
    title: 'Success',
    variant: 'success',
    labels: {
      close: 'Close',
    },
  });

  const errorAlert = useAlert({
    title: 'Error',
    variant: 'error',
    labels: {
      close: 'Close',
    },
  });

  const handleNavLinkClick = (option: NavLinks) => {
    option.UniqueComponentId !== 'download_user_guide'
      ? navigate(option.MetaData.url)
      : downloadFileFromCms({ url: option?.MetaData?.url, filename: 'User-Guide.pdf' });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      css={[
        styles.container,
        !isLogin &&
          (isMobile
            ? { display: 'none' }
            : { background: 'white', borderBottom: '1px solid gray' }),
      ]}
    >
      {!!isLogin && (
        <MenuIcon
          css={styles.hamburgerLogo}
          onClick={() => setIsHamburgerMenuOpen(true)}
          className='hamburger'
        />
      )}
      <img
        src={isLogin ? STATIC_IMAGES.VIDAL_LOGO_LIGHT : STATIC_IMAGES.VIDAL_LOGO_DARK}
        alt='Vidal_logo'
        css={styles.logo}
        onClick={() => {
          navigate(isLogin ? '/' : '/login');
        }}
      />

      {!!isLogin && (
        <>
          <div css={styles.navData}>
            {!isCmsDataLoading &&
              !!navLinks &&
              !isMobile &&
              navLinks.map(option => (
                <Text
                  key={option.MetaData.url}
                  variant='body-1'
                  color='white'
                  onClick={() => handleNavLinkClick(option)}
                  css={styles.pointer}
                >
                  {option.Title}
                </Text>
              ))}
          </div>

          <div css={styles.rightPanel}>
            <div css={styles.logout} />
            <div css={styles.profileWrapper} ref={profileRef}>
              <div
                css={styles.pointer}
                onClick={e => {
                  e.stopPropagation();
                  setProfileOpen(prev => !prev);
                }}
              >
                <PersonOutline color='white' size={24} />
                <ArrowChevronDown color='white' />
              </div>

              {isProfileOpen && (
                <ProfileSelection
                  onAction={() => setProfileOpen(false)}
                  setIsChangePasswordOpen={setIsChangePasswordOpen}
                />
              )}
            </div>

            <Sheet
              open={isHamburgerMenuOpen}
              onClose={() => setIsHamburgerMenuOpen(false)}
              direction='left'
            >
              <ProfileSelection
                onAction={() => setIsHamburgerMenuOpen(false)}
                setIsChangePasswordOpen={setIsChangePasswordOpen}
              />
            </Sheet>

            <Sheet
              open={isChangePasswordOpen}
              onClose={() => setIsChangePasswordOpen(false)}
              direction={isMobile ? 'bottom' : 'right'}
            >
              <CreateNewPassword
                onClose={() => setIsChangePasswordOpen(false)}
                successAlert={successAlert}
                errorAlert={errorAlert}
              />
            </Sheet>
          </div>
        </>
      )}
    </div>
  );
};

export default Navbar;
